import { TimezonesList } from "@/common/timezones";

export function isMac(): boolean {
	return /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform);
}

export function isMobile(): boolean {
	if (typeof window !== 'undefined') {
		if (
			window.navigator.userAgent.match(/Android/i) ||
			window.navigator.userAgent.match(/webOS/i) ||
			window.navigator.userAgent.match(/iPhone/i) ||
			window.navigator.userAgent.match(/iPad/i) ||
			window.navigator.userAgent.match(/iPod/i) ||
			window.navigator.userAgent.match(/BlackBerry/i) ||
			window.navigator.userAgent.match(/Windows Phone/i)
		) {
			return true;
		} else {
			return false;
		}
	}
	return false;
};

const KEY_MODIFIERS = ['ctrl', 'alt', 'shift'];

export function keyForDisplay(key: string): string {
	const lowerKey = key.trim().toLowerCase();

	if (KEY_MODIFIERS.includes(lowerKey)) {
		return lowerKey.toUpperCase();
	}

	if (lowerKey.length > 1) {
		return lowerKey.substring(0, 1).toUpperCase() + lowerKey.substring(1);
	}

	return key.trim();
}

export function kbShortcutStringToComponents(
	shortcut: string,
	localize: boolean = true,	// Converts Mod to system rep
	forDisplay: boolean = false // Make the output pretty
): Array<string> {
	const result: Array<string> = [];

	const components = shortcut.split('+');

	for (let component of components) {
		component = component.trim();

		if (component.length === 0) {
			continue;
		}

		if (localize && component.toLowerCase() === 'mod') {
			let ctrl = (isMac()) ? '⌘' : 'ctrl';

			if (forDisplay) {
				ctrl = keyForDisplay(ctrl);
			}
			result.push(ctrl);
		} else if (component.toLowerCase() === 'enter') {
			result.push('⏎');
		} else if (component.toLowerCase() === 'delete') {
			result.push('del');
		} else {
			if (forDisplay) {
				component = keyForDisplay(component);
			}
			result.push(component);
		}
	}

	return result;
}

export function copyTextToClipboard(text: string): Promise<any> {
	const data = [
		//@ts-ignore
		new ClipboardItem(
			{
				"text/plain": new Blob(
					[text],
					{ type: "text/plain" }
				)
			}
		)
	];

	//@ts-ignore
	return window.navigator.clipboard.write(data);
}

// https://github.com/ueberdosis/tiptap/blob/develop/packages/core/src/utilities/isRegExp.ts#L1
export function isRegExp(value: any): value is RegExp {
	return Object.prototype.toString.call(value) === '[object RegExp]';
}

/**
 * https://github.com/ueberdosis/tiptap/blob/develop/packages/core/src/utilities/objectIncludes.ts#L8
 * Check if object1 includes object2
 * @param object1 Object
 * @param object2 Object
 */
export function objectIncludes(
	object1: Record<string, any>,
	object2: Record<string, any>,
	options: { strict: boolean } = { strict: true },
): boolean {
	const keys = Object.keys(object2);

	if (!keys.length) {
		return true;
	}

	return keys.every(key => {
		if (options.strict) {
			return object2[key] === object1[key];
		}

		if (isRegExp(object2[key])) {
			return object2[key].test(object1[key]);
		}

		return object2[key] === object1[key];
	})
}

const VALID_SUBDOMAIN_CHARSET = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789-_';

/**
 * Takes a string and makes it safe for use
 * as a subdomain
 */
export function stringToSubdomain(input: string): string {
	input = input.replace(/ /g, '-');
	input = input.toLowerCase();
	input = input.trim();

	let result = '';
	for (let c of input) {
		if (VALID_SUBDOMAIN_CHARSET.includes(c)) {
			result += c;
		}
	}

	return result;
}


export function indexToAlpha(index: number): string {
	return 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[index % 26];
}


/* Randomize array in-place using Durstenfeld shuffle algorithm */
export function inPlaceShuffleArray<T>(array: Array<T>): void {
	for (let i = array.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[array[i], array[j]] = [array[j], array[i]];
	}
}


/**
 * 	Given a dom node, get the vue component, if there is one
*/
export function getVueInstanceFromDomNode(domNode: any): any | null {
	if (domNode.__vue__ !== undefined) {
		return domNode.__vue__;
	}

	return null;
}


export function getFirstVueInstanceInDescendants(domNode: Node): any | null {
	if (domNode.firstChild === null) {
		return null;
	}

	const inst = getVueInstanceFromDomNode(domNode.firstChild);

	if (inst) {
		return inst;
	}

	return getFirstVueInstanceInDescendants(domNode.firstChild);
}

/**
 * Used to exhaust options in a switch a statement for an enum typically
 */
export function assertUnreachable(x: never): never {
	throw new Error("Didn't expect to get here");
}


/**
 * Uses canvas.measureText to compute and return the width of the given text of given font in pixels.
 *
 * @param {String} text The text to be rendered.
 * @param {String} font The css font descriptor that text is to be rendered with (e.g. "bold 14px verdana").
 *
 * @see https://stackoverflow.com/questions/118241/calculate-text-width-with-javascript/21015393#21015393
 */
export function getTextWidth(text: string, font: string): number {
	// re-use canvas object for better performance
	// @ts-ignore
	const canvas: HTMLCanvasElement = getTextWidth.canvas || (getTextWidth.canvas = document.createElement("canvas"));
	const context = canvas.getContext("2d");

	if (!context) {
		return 100;
	}

	context.font = font;
	const metrics = context.measureText(text);
	return metrics.width;
}


function getCssStyle(element: HTMLElement, prop: string): string {
	return window.getComputedStyle(element, null).getPropertyValue(prop);
}


export function getElementFont(el: HTMLElement = document.body): string {
	const fontWeight = getCssStyle(el, 'font-weight') || 'normal';
	const fontSize = getCssStyle(el, 'font-size') || '16px';
	const fontFamily = getCssStyle(el, 'font-family') || 'Times New Roman';

	return `${fontWeight} ${fontSize} ${fontFamily}`;
}


export function getTimezoneChoices(): Array<any> { //DropdownChoice<string>> {
	return TimezonesList.map((tz: string) => { return { label: tz.replace(/\_/g, ' '), value: tz }; });
}
